import { useState, useEffect, useRef, useCallback } from 'react'
import { ticketsAPI } from '../services/api'
import { shouldPollConversation } from '../utils/conversationPolling'

/**
 * Hook for managing ticket details with conditional polling.
 * Polls on a 3s cadence when shouldPollConversation returns true; otherwise idle.
 * The turn stream gates polling via pausePolling/resumePolling to avoid races.
 *
 * @param {Object} options
 * @param {number|string|null} options.ticketId - Ticket ID to fetch
 * @param {Function} options.onTicketUpdated - Callback when ticket is updated (e.g., archived, deleted)
 * @param {string|null} options.currentUserEmail - Logged-in user email; used by shouldPollConversation
 * @returns {Object} Ticket details state and controls
 */
export const useTicketDetails = ({ ticketId = null, onTicketUpdated = null, currentUserEmail = null } = {}) => {
  // Core state
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Refs for polling
  const pollingIntervalRef = useRef(null)
  const ticketIdRef = useRef(ticketId)
  const pendingRequestRef = useRef(null)
  const pollingPausedRef = useRef(false)
  const resumeTimerRef = useRef(null)

  // Update ref when ticketId changes
  useEffect(() => {
    ticketIdRef.current = ticketId
  }, [ticketId])

  // Compute isProcessing from ticket state
  // Agent is processing when assigned to agent and not in a terminal state
  const isProcessing = (ticket?.role === 'agent' || ticket?.role === 'system') && ticket?.status !== 'complete' && ticket?.status !== 'error'

  // Fetch ticket details
  const fetchTicket = useCallback(async (options = {}) => {
    const { fromPolling = false } = options
    const currentTicketId = ticketIdRef.current

    if (!currentTicketId) {
      setTicket(null)
      setLoading(false)
      return
    }

    // Request deduplication
    const requestSignature = `details-${currentTicketId}`
    if (pendingRequestRef.current === requestSignature) {
      return
    }
    pendingRequestRef.current = requestSignature

    try {
      if (!fromPolling) {
        setLoading(true)
      }

      const response = await ticketsAPI.getTicket(currentTicketId)
      setError(null)

      // Skip state update if polling was paused while request was in-flight
      if (fromPolling && pollingPausedRef.current) {
        return
      }

      // Only update if data actually changed
      setTicket(prevTicket => {
        if (!prevTicket) return response

        // Compare key fields to avoid unnecessary re-renders
        const hasChanges =
          prevTicket.status !== response.status ||
          prevTicket.role !== response.role ||
          prevTicket.modified_at !== response.modified_at ||
          prevTicket.current_action !== response.current_action ||
          prevTicket.active_child !== response.active_child ||
          JSON.stringify(prevTicket.conversation_thread) !== JSON.stringify(response.conversation_thread) ||
          JSON.stringify(prevTicket.child_tickets) !== JSON.stringify(response.child_tickets)

        return hasChanges ? response : prevTicket
      })

    } catch (err) {
      console.error('Failed to fetch ticket details:', err)

      // Stop polling on auth errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        pendingRequestRef.current = null
        return
      }

      // Handle 404 - ticket might have been deleted
      if (err.response?.status === 404) {
        setTicket(null)
        if (onTicketUpdated) {
          onTicketUpdated('deleted', currentTicketId)
        }
      }

      if (!fromPolling) {
        setError(err)
      }
    } finally {
      pendingRequestRef.current = null
      if (!fromPolling) {
        setLoading(false)
      }
    }
  }, [onTicketUpdated])

  // Manual refresh
  const refresh = useCallback(() => {
    fetchTicket()
  }, [fetchTicket])

  // Trigger immediate poll (bypasses interval)
  const triggerImmediatePoll = useCallback(() => {
    fetchTicket({ fromPolling: true })
  }, [fetchTicket])

  // Pause polling during user interactions
  const pausePolling = useCallback(() => {
    pollingPausedRef.current = true
    // Clear any pending resume timer
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current)
      resumeTimerRef.current = null
    }
  }, [])

  // Resume polling immediately
  const resumePolling = useCallback(() => {
    pollingPausedRef.current = false
    // Clear any pending resume timer
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current)
      resumeTimerRef.current = null
    }
  }, [])

  // Resume polling after a delay (useful after form submission)
  const resumePollingAfterDelay = useCallback((delayMs = 2000) => {
    // Clear any existing timer
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current)
    }
    resumeTimerRef.current = setTimeout(() => {
      pollingPausedRef.current = false
      resumeTimerRef.current = null
      // Trigger immediate poll after resuming
      fetchTicket({ fromPolling: true })
    }, delayMs)
  }, [fetchTicket])

  // No-op: kept for API compatibility with callers (was used for adaptive polling interval)
  const onImmediateThinkingChange = useCallback(() => {}, [])

  // Fetch when ticketId changes
  useEffect(() => {
    if (ticketId) {
      pollingPausedRef.current = false // Reset pause state — prevents stale pause from a previous ticket blocking polling
      setTicket(null) // Clear previous ticket
      fetchTicket()
    } else {
      setTicket(null)
      setLoading(false)
      setError(null)
    }
  }, [ticketId, fetchTicket])

  // Optimistically append an assistant message to the local ticket state so
  // it appears instantly after streaming completes, before the server SSE refresh.
  const injectOptimisticMessage = useCallback((message) => {
    setTicket(prev => {
      if (!prev) return prev

      // When a child is active, tag the merged thread copy with is_child,{childType} so it
      // receives the same visual grouping as DB-fetched child messages (mirrors TicketResponse.from_ticket()).
      let mergedMessage = message
      if (prev.active_child && prev.child_tickets) {
        const childType = prev.child_tickets.find(c => c.id === prev.active_child)?.type
        if (childType) {
          const existingTags = message.messageMetadata?.tags || ''
          const tagPrefix = `is_child,${childType}`
          mergedMessage = {
            ...message,
            messageMetadata: {
              ...(message.messageMetadata || {}),
              tags: existingTags ? `${tagPrefix},${existingTags}` : tagPrefix,
            },
          }
        }
      }

      return {
        ...prev,
        // Always update the parent thread (untagged)
        conversation_thread: [...(prev.conversation_thread || []), message],
        // Also update merged thread if it exists (tagged with is_child when active_child is set)
        ...(prev.merged_conversation_thread && {
          merged_conversation_thread: [...prev.merged_conversation_thread, mergedMessage],
        }),
        // Also update active child's thread if one is active
        ...(prev.active_child && prev.child_tickets && {
          child_tickets: prev.child_tickets.map(child =>
            child.id === prev.active_child
              ? { ...child, conversation_thread: [...(child.conversation_thread || []), message] }
              : child
          ),
        }),
      }
    })
  }, [])

  // Inject a child ticket created during streaming (from child_ticket_created SSE event).
  // Sets active_child so subsequent streamed messages are routed to the correct child thread.
  const injectChildTicket = useCallback((childTicket) => {
    setTicket(prev => {
      if (!prev) return prev
      return {
        ...prev,
        child_tickets: [...(prev.child_tickets || []), childTicket],
        active_child: childTicket.id,
      }
    })
  }, [])

  // Merge ticket_data fields from message_complete into local state.
  const injectTicketDataUpdate = useCallback((ticketData) => {
    if (!ticketData || Object.keys(ticketData).length === 0) return
    setTicket(prev => {
      if (!prev) return prev
      return { ...prev, data: { ...(prev.data || {}), ...ticketData } }
    })
  }, [])

  // Patch ticket status/role immediately on message_complete (before server round-trip).
  // Also patches effective_status/effective_role and the active child's status/role so
  // that displayTicket (which resolves to the active child when one is set) reflects
  // the optimistic state immediately.
  // Notifies onTicketUpdated with a patch object so siblings (e.g. TicketList) can
  // update their row without a refetch.
  const injectTicketStatusUpdate = useCallback((status, role) => {
    setTicket(prev => {
      if (!prev) return prev
      // When control returns to the user the sub-agent's turn is done — clear active_child
      // so getDisplayTicket() resolves to the parent (which has assigned_to/group_chat set).
      const clearActiveChild = role === 'user' || role === 'external'
      return {
        ...prev,
        ...(status && { status }),
        ...(role && { role }),
        ...(status && { effective_status: status }),
        ...(role && { effective_role: role }),
        ...(clearActiveChild && { active_child: null }),
        ...(!clearActiveChild && prev.active_child && prev.child_tickets && (status || role) && {
          child_tickets: prev.child_tickets.map(child =>
            child.id === prev.active_child
              ? { ...child, ...(status && { status }), ...(role && { role }) }
              : child
          ),
        }),
      }
    })
    if (onTicketUpdated && (status || role)) {
      onTicketUpdated({ status, role })
    }
  }, [onTicketUpdated])

  // Conditional polling controller.
  // Polls GET /tickets/{id} on a 3s cadence ONLY when the conversation needs it
  // (grouped, watching an unassigned/incomplete ticket, or own ticket still processing).
  // pollingPausedRef gates it: the turn stream pauses polling for the whole open
  // request (and 2s after), so polling never races an in-progress streamed turn.
  // Reuses the existing pollingIntervalRef (also cleared by the 401/403 handler).
  const needsPolling = shouldPollConversation(ticket, currentUserEmail)

  useEffect(() => {
    if (!ticketId || !needsPolling) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      return
    }
    pollingIntervalRef.current = setInterval(() => {
      if (pollingPausedRef.current) return
      fetchTicket({ fromPolling: true })
    }, 3000)
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [ticketId, needsPolling, fetchTicket])

  // Cleanup resume timer on unmount
  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current)
        resumeTimerRef.current = null
      }
    }
  }, [])

  return {
    // Data
    ticket,
    loading,
    error,
    isProcessing,

    // Actions
    refresh,
    triggerImmediatePoll,

    // Polling control (for user interactions)
    pausePolling,
    resumePolling,
    resumePollingAfterDelay,
    onImmediateThinkingChange,

    injectOptimisticMessage,
    injectTicketStatusUpdate,
    injectChildTicket,
    injectTicketDataUpdate,
  }
}

export default useTicketDetails
